/**
 * TablesPage — Table management orchestrator.
 *
 * This page is a thin orchestrator. All sub-components live in:
 *   @/components/tables/
 *     - MesasGrid.js      — Table grid + QR code management
 *     - TableDetail.js    — Full table account detail view
 *     - CuentasTab.js     — Cuentas history list
 *     - CancelacionesTab.js — Cancellation history
 *     - FacturasTab.js    — Invoice list
 *     - TablePayment.js   — Payment & discount dialogs
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { toast } from "sonner";
import { Settings } from "lucide-react";

import { MesasGrid } from "@/components/tables/MesasGrid";
import { TableDetail } from "@/components/tables/TableDetail";
import { CuentasTab } from "@/components/tables/CuentasTab";
import { CancelacionesTab } from "@/components/tables/CancelacionesTab";
import { FacturasTab } from "@/components/tables/FacturasTab";

const TABS = [
  { key: "mesas", label: "MESAS" },
  { key: "cuentas", label: "CUENTAS" },
  { key: "cancelaciones", label: "CANCELACIONES" },
  { key: "facturas", label: "FACTURAS" },
];

export default function TablesPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("mesas");
  const [selectedTable, setSelectedTable] = useState(null);
  const [restaurantSlug, setRestaurantSlug] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [restaurantData, setRestaurantData] = useState(null);

  const fetchTables = useCallback(async () => {
    try { const res = await authAxiosRef.current.get("/tables"); setTables(res.data); }
    catch { toast.error("Error cargando mesas"); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 5000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/restaurant").then(res => {
      const data = res.data?.data || res.data;
      setRestaurantData(data);
      if (data?.slug) setRestaurantSlug(data.slug);
      if (data?.custom_domain) setCustomDomain(data.custom_domain);
    }).catch(() => {});
  }, []);

  if (loading) return <MainLayout><LoadingScreen /></MainLayout>;

  if (selectedTable) {
    return (
      <MainLayout>
        <TableDetail
          table={selectedTable}
          onBack={() => setSelectedTable(null)}
          onRefresh={fetchTables}
          restaurantSlug={restaurantSlug}
          customDomain={customDomain}
          restaurant={restaurantData}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="tables-page">
        <div className="border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex gap-3 sm:gap-6 flex-1 overflow-x-auto scrollbar-hide">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`pb-3 text-xs sm:text-sm font-bold uppercase tracking-wide transition-colors border-b-2 whitespace-nowrap shrink-0 ${activeTab === tab.key ? "text-emerald-700 border-emerald-600" : "text-gray-500 border-transparent hover:text-gray-700"}`}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => navigate("/settings?tab=operacoes")}
              className="mb-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
              title="Configuracoes de operacoes (pagamento na mesa)"
              data-testid="tables-settings-gear"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>

        {activeTab === "mesas" && (
          <MesasGrid
            tables={tables}
            setTables={setTables}
            onSelectTable={setSelectedTable}
            onRefresh={fetchTables}
            restaurantSlug={restaurantSlug}
            customDomain={customDomain}
            restaurant={restaurantData}
          />
        )}
        {activeTab === "cuentas" && <CuentasTab />}
        {activeTab === "cancelaciones" && <CancelacionesTab />}
        {activeTab === "facturas" && <FacturasTab />}
      </div>
    </MainLayout>
  );
}
